import type { Order } from '../types';
import { getAmountPaid, getOrderTotalFromItems } from './orderUtils';

/** Discount-aware order total minus payments already recorded (dollars, 2dp). */
export function getOrderAmountDue(order: Pick<Order, 'items' | 'payments' | 'totalAmount' | 'discountMethod' | 'manualDiscount' | 'firstTableDiscountAmount' | 'couponDiscountAmount' | 'residentDiscountAmount' | 'appliedReward' | 'orderType' | 'deliveryCharge' | 'discountAmount'>): number {
  const computed = getOrderTotalFromItems(order as Order);
  const orderTotal = computed > 0.005 ? computed : (order.totalAmount ?? 0);
  const paid = getAmountPaid(order as Order);
  return Math.round((orderTotal - paid) * 100) / 100;
}

/** Discount / total fields that PaymentModal and POS sync from a live order snapshot. */
export type DiscountSyncFields = Pick<
  Order,
  | 'manualDiscount'
  | 'discountMethod'
  | 'firstTableDiscountAmount'
  | 'couponCode'
  | 'couponCampaignId'
  | 'couponDiscountType'
  | 'couponDiscountValue'
  | 'couponDiscountAmount'
  | 'residentDiscountPercent'
  | 'residentDiscountAmount'
  | 'appliedReward'
  | 'discountAmount'
  | 'subtotal'
  | 'taxAmount'
  | 'totalAmount'
>;

export function pickDiscountSyncFields(from: Order): DiscountSyncFields {
  return {
    manualDiscount: from.manualDiscount,
    discountMethod: from.discountMethod,
    firstTableDiscountAmount: from.firstTableDiscountAmount,
    couponCode: from.couponCode,
    couponCampaignId: from.couponCampaignId,
    couponDiscountType: from.couponDiscountType,
    couponDiscountValue: from.couponDiscountValue,
    couponDiscountAmount: from.couponDiscountAmount,
    residentDiscountPercent: from.residentDiscountPercent,
    residentDiscountAmount: from.residentDiscountAmount,
    appliedReward: from.appliedReward,
    discountAmount: from.discountAmount,
    subtotal: from.subtotal,
    taxAmount: from.taxAmount,
    totalAmount: from.totalAmount,
  };
}

export function discountFieldsDiffer(a: Order, b: Order): boolean {
  return (
    (a.manualDiscount ?? 0) !== (b.manualDiscount ?? 0) ||
    (a.firstTableDiscountAmount ?? 0) !== (b.firstTableDiscountAmount ?? 0) ||
    (a.couponDiscountAmount ?? 0) !== (b.couponDiscountAmount ?? 0) ||
    (a.residentDiscountAmount ?? 0) !== (b.residentDiscountAmount ?? 0) ||
    (a.discountAmount ?? 0) !== (b.discountAmount ?? 0) ||
    a.discountMethod !== b.discountMethod ||
    (a.appliedReward?.discountValue ?? 0) !== (b.appliedReward?.discountValue ?? 0) ||
    Math.abs((a.totalAmount ?? 0) - (b.totalAmount ?? 0)) > 0.005
  );
}

/**
 * True when adopting `live` discount fields would not raise amount due vs `local`.
 * Blocks the Pay-open race where a stale pre-discount Firestore snapshot wipes a
 * locally applied discount and EFTPOS charges the face value.
 */
export function liveDiscountIsSafeToAdopt(local: Order, live: Order): boolean {
  const localDue = getOrderAmountDue(local);
  const liveDue = getOrderAmountDue(live);
  return liveDue <= localDue + 0.005;
}

/**
 * Prefer the working copy with the lower amount due (more discount applied).
 * Used at card-charge time so a briefly stale frozen copy cannot overcharge.
 */
export function preferDiscountedOrderForCharge(primary: Order, secondary: Order | null | undefined): Order {
  if (!secondary || secondary.id !== primary.id) return primary;
  const primaryDue = getOrderAmountDue(primary);
  const secondaryDue = getOrderAmountDue(secondary);
  if (secondaryDue + 0.005 < primaryDue) return secondary;
  return primary;
}

export type ResolveCardChargeInput = {
  /** Working order used for due (must include discount fields). */
  order: Order;
  /** Cashier-entered or prefilled card amount (before tip). */
  requestedAmount: number;
  /**
   * When paying a staged sub-bill, the sub-bill face total.
   * Still capped by discounted order amount due so remainder/select-all cannot overcharge.
   */
  stagedSubBillTotal?: number | null;
};

export type ResolveCardChargeResult =
  | { ok: true; chargeAmount: number; amountDue: number }
  | { ok: false; reason: string; amountDue: number; chargeAmount?: number };

/**
 * Single source of truth for the dollar amount sent to SmartConnect (before tip).
 * Never charges more than the discounted amount due on the order.
 */
export function resolveCardChargeAmount(input: ResolveCardChargeInput): ResolveCardChargeResult {
  const amountDue = getOrderAmountDue(input.order);
  if (amountDue <= 0.005) {
    return { ok: false, reason: 'Order has no balance due.', amountDue };
  }

  const staged =
    input.stagedSubBillTotal != null && Number.isFinite(input.stagedSubBillTotal)
      ? Math.round(input.stagedSubBillTotal * 100) / 100
      : null;

  let requested = Math.round((Number(input.requestedAmount) || 0) * 100) / 100;
  if (staged != null) {
    // Sub-bill face value, but never above remaining discounted due.
    requested = Math.min(staged, amountDue);
  }

  if (requested <= 0.005) {
    return { ok: false, reason: 'Payment amount must be greater than $0.00.', amountDue };
  }
  if (requested > amountDue + 0.005) {
    return {
      ok: false,
      reason: `Card amount ($${requested.toFixed(2)}) cannot exceed amount due ($${amountDue.toFixed(2)}).`,
      amountDue,
      chargeAmount: requested,
    };
  }

  return {
    ok: true,
    chargeAmount: Math.min(requested, amountDue),
    amountDue,
  };
}
